// ============================================================================
// Orders.jsx — Buyer order history + order tracking + download + disputes
// Route: /orders (protected)
// ============================================================================

import { useState, useEffect } from 'react'
import { useAuth } from '../hooks/useAuth'
import { Link } from 'react-router-dom'

const API_URL = import.meta.env.VITE_API_URL || ''

const STATUS_COLORS = {
  pending_payment: 'bg-yellow-100 text-yellow-700',
  paid: 'bg-blue-100 text-blue-700',
  processing: 'bg-blue-100 text-blue-700',
  shipped: 'bg-purple-100 text-purple-700',
  delivered: 'bg-emerald-100 text-emerald-700',
  completed: 'bg-emerald-100 text-emerald-700',
  cancelled: 'bg-slate-100 text-slate-500',
  disputed: 'bg-red-100 text-red-700',
  refunded: 'bg-slate-100 text-slate-500',
}

const STATUS_LABELS = {
  pending_payment: 'Awaiting Payment',
  paid: 'Paid',
  processing: 'Processing',
  shipped: 'Shipped',
  delivered: 'Delivered',
  completed: 'Completed',
  cancelled: 'Cancelled',
  disputed: 'Disputed',
  refunded: 'Refunded',
}

export default function Orders() {
  const { user } = useAuth()
  const [orders, setOrders] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedOrder, setSelectedOrder] = useState(null)
  const [disputeForm, setDisputeForm] = useState(null)
  const [reviewForm, setReviewForm] = useState(null)

  useEffect(() => {
    if (!user?.email) return
    fetchOrders()
  }, [user?.email])

  const fetchOrders = async () => {
    try {
      setLoading(true)
      const res = await fetch(`${API_URL}/orders/my?email=${encodeURIComponent(user.email)}&role=buyer`)
      const data = await res.json()
      setOrders(data.orders || [])
    } catch (e) {
      console.error('Fetch orders error:', e)
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = async (orderId) => {
    if (!confirm('Cancel this order? You will receive a full refund.')) return
    try {
      const res = await fetch(`${API_URL}/orders/${orderId}/cancel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-user-email': user.email },
        body: JSON.stringify({ reason: 'Buyer cancelled' })
      })
      if (res.ok) fetchOrders()
    } catch (e) { console.error(e) }
  }

  const handleOpenDispute = async (e) => {
    e.preventDefault()
    if (!disputeForm) return
    try {
      const res = await fetch(`${API_URL}/orders/${disputeForm.orderId}/dispute`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-user-email': user.email },
        body: JSON.stringify({ reason: disputeForm.reason, description: disputeForm.description })
      })
      if (res.ok) { setDisputeForm(null); fetchOrders() }
    } catch (e) { console.error(e) }
  }

  const handleSubmitReview = async (e) => {
    e.preventDefault()
    if (!reviewForm) return
    try {
      const res = await fetch(`${API_URL}/marketplace/listings/${reviewForm.listingId}/review`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-user-email': user.email },
        body: JSON.stringify({ rating: reviewForm.rating, review_text: reviewForm.text })
      })
      if (res.ok) { setReviewForm(null); alert('Review submitted!') }
    } catch (e) { console.error(e) }
  }

  const timeAgo = (d) => {
    if (!d) return ''
    const s = Math.floor((Date.now() - new Date(d)) / 1000)
    if (s < 3600) return `${Math.floor(s / 60)}m ago`
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`
    return new Date(d).toLocaleDateString()
  }

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Nav */}
      <nav className="border-b border-slate-800 px-6 py-3 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-zenna-violet to-zenna-cyan flex items-center justify-center">
            <span className="text-white font-bold text-sm">Z</span>
          </div>
          <span className="text-white font-semibold">Zenna</span>
        </Link>
        <div className="flex items-center gap-3 text-sm">
          <Link to="/dashboard" className="text-slate-400 hover:text-white transition">Dashboard</Link>
          <Link to="/marketplace" className="text-slate-400 hover:text-white transition">Shop</Link>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold text-white mb-2">My Orders</h1>
        <p className="text-sm text-slate-500 mb-6">Your purchase history and downloads</p>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-zenna-violet border-t-transparent rounded-full animate-spin" />
          </div>
        ) : orders.length === 0 ? (
          <div className="text-center py-20">
            <div className="text-5xl mb-3">🛍️</div>
            <h3 className="text-lg font-medium text-white mb-2">No orders yet</h3>
            <p className="text-sm text-slate-500 mb-4">Browse the marketplace to find your first design!</p>
            <Link to="/marketplace" className="inline-block px-6 py-2.5 bg-gradient-to-r from-zenna-violet to-zenna-cyan text-white rounded-xl hover:opacity-90 transition">
              Browse Shop
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {orders.map(order => {
              const snap = order.listing_snapshot || {}
              return (
                <div key={order.id} className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
                  <div className="flex items-center gap-4 p-4">
                    {/* Thumbnail */}
                    {snap.thumbnail ? (
                      <img src={snap.thumbnail} alt="" className="w-16 h-16 rounded-lg object-cover shrink-0" />
                    ) : (
                      <div className="w-16 h-16 rounded-lg bg-slate-700 flex items-center justify-center text-2xl shrink-0">🎨</div>
                    )}

                    {/* Details */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-medium text-white truncate">{snap.title || 'Design'}</span>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${STATUS_COLORS[order.status] || 'bg-slate-100 text-slate-500'}`}>
                          {STATUS_LABELS[order.status] || order.status}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-slate-500">
                        <span>Order #{order.order_number}</span>
                        <span>{timeAgo(order.created_at)}</span>
                        <span className="text-emerald-400 font-medium">${(order.total_paid || 0).toFixed(2)}</span>
                        {order.license_type && <span className="capitalize">{order.license_type} license</span>}
                      </div>

                      {/* Tracking */}
                      {order.tracking_number && (
                        <div className="mt-2 text-xs text-slate-400">
                          📦 Tracking: <span className="text-white font-mono">{order.tracking_number}</span>
                          {order.tracking_carrier && <span className="ml-1">({order.tracking_carrier})</span>}
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex flex-col gap-1.5 shrink-0">
                      {/* Download button for digital items — uses secure signed URL */}
                      {(order.download_url || order.item_type === 'digital') && ['paid', 'delivered', 'completed'].includes(order.status) && (
                        <button
                          onClick={async () => {
                            try {
                              const res = await fetch(`${API_URL}/orders/${order.id}/download`, {
                                headers: { 'x-user-email': user.email }
                              })
                              const data = await res.json()
                              if (data.download_url) {
                                window.open(data.download_url, '_blank')
                              } else {
                                alert('Download not available: ' + (data.error || 'Unknown error'))
                              }
                            } catch (e) { alert('Download failed. Try again.') }
                          }}
                          className="px-3 py-1.5 text-xs font-medium bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition text-center"
                        >
                          Download
                        </button>
                      )}

                      {/* Cancel (only for pending/paid orders) */}
                      {['pending_payment', 'paid', 'processing'].includes(order.status) && (
                        <button
                          onClick={() => handleCancel(order.id)}
                          className="px-3 py-1.5 text-xs text-red-400 hover:bg-red-400/10 rounded-lg transition"
                        >
                          Cancel
                        </button>
                      )}

                      {/* Dispute (only for shipped/delivered) */}
                      {['shipped', 'delivered', 'completed'].includes(order.status) && !order.dispute_id && (
                        <button
                          onClick={() => setDisputeForm({ orderId: order.id, reason: '', description: '' })}
                          className="px-3 py-1.5 text-xs text-amber-400 hover:bg-amber-400/10 rounded-lg transition"
                        >
                          Report Issue
                        </button>
                      )}

                      {/* Review (only for completed/delivered) */}
                      {['delivered', 'completed'].includes(order.status) && order.listing_id && (
                        <button
                          onClick={() => setReviewForm({ listingId: order.listing_id, rating: 5, text: '' })}
                          className="px-3 py-1.5 text-xs text-zenna-violet hover:bg-zenna-violet/10 rounded-lg transition"
                        >
                          Leave Review
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Dispute Modal */}
      {disputeForm && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setDisputeForm(null)}>
          <div className="bg-slate-800 rounded-2xl max-w-md w-full border border-slate-700 p-5" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-white mb-3">Report an Issue</h3>
            <form onSubmit={handleOpenDispute} className="space-y-3">
              <select
                value={disputeForm.reason}
                onChange={e => setDisputeForm(d => ({ ...d, reason: e.target.value }))}
                required
                className="w-full px-3 py-2 rounded-lg bg-slate-900 border border-slate-700 text-white text-sm"
              >
                <option value="">Select a reason...</option>
                <option value="not_as_described">Not as described</option>
                <option value="file_corrupted">File corrupted or won't open</option>
                <option value="wrong_item">Wrong item received</option>
                <option value="not_received">Item not received</option>
                <option value="quality_issue">Quality issue</option>
                <option value="other">Other</option>
              </select>
              <textarea
                value={disputeForm.description}
                onChange={e => setDisputeForm(d => ({ ...d, description: e.target.value }))}
                placeholder="Describe the issue..."
                required
                rows={3}
                className="w-full px-3 py-2 rounded-lg bg-slate-900 border border-slate-700 text-white text-sm resize-none"
              />
              <div className="flex gap-2 justify-end">
                <button type="button" onClick={() => setDisputeForm(null)} className="px-4 py-2 text-sm text-slate-400 hover:text-white transition">Cancel</button>
                <button type="submit" className="px-4 py-2 text-sm bg-red-500 text-white rounded-lg hover:bg-red-600 transition">Submit Dispute</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Review Modal */}
      {reviewForm && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setReviewForm(null)}>
          <div className="bg-slate-800 rounded-2xl max-w-md w-full border border-slate-700 p-5" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-white mb-3">Leave a Review</h3>
            <form onSubmit={handleSubmitReview} className="space-y-3">
              <div>
                <label className="text-xs text-slate-400 block mb-1">Rating</label>
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map(n => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setReviewForm(r => ({ ...r, rating: n }))}
                      className={`text-2xl transition ${n <= reviewForm.rating ? 'text-amber-400' : 'text-slate-600'}`}
                    >
                      ★
                    </button>
                  ))}
                </div>
              </div>
              <textarea
                value={reviewForm.text}
                onChange={e => setReviewForm(r => ({ ...r, text: e.target.value }))}
                placeholder="Share your experience... (optional)"
                rows={3}
                className="w-full px-3 py-2 rounded-lg bg-slate-900 border border-slate-700 text-white text-sm resize-none"
              />
              <div className="flex gap-2 justify-end">
                <button type="button" onClick={() => setReviewForm(null)} className="px-4 py-2 text-sm text-slate-400 hover:text-white transition">Cancel</button>
                <button type="submit" className="px-4 py-2 text-sm bg-zenna-violet text-white rounded-lg hover:bg-zenna-violet/80 transition">Submit Review</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
